import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import CameraManagement from './CameraManagment';
import AlertList from './AlertList';
import { getCameras, getAlerts, deleteAlert } from '../api';
import { logoutUser } from '../services/auth';
import useWebSocket from '../hooks/useWebSocket';

const Dashboard = ({ user }) => {
  const [cameras, setCameras] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [wsAlerts, setWsAlerts] = useState([]);

  // WebSocket integration for real-time alerts
  const {
    connectionState,
    isConnected,
    alerts: realTimeAlerts
  } = useWebSocket(user?.id, {
    autoConnect: true,
    onNewAlert: (alertData) => {
      console.log('🚨 [DASHBOARD] New alert received:', alertData);
      // Alert appears immediately on website - no delay
      if (alertData.status === 'detected') {
        console.log('⚡ [DASHBOARD] Fresh alert detected - showing immediately');
      }
    },
    onAlertUpdate: (alertData) => {
      console.log('📝 [DASHBOARD] Alert update received:', alertData);
      if (alertData.status === 'processed') {
        console.log('✅ [DASHBOARD] Alert fully processed - database saved and WhatsApp sent');
      }
    },
    onSystemMessage: (messageData) => {
      console.log('📢 [DASHBOARD] System message:', messageData.message);
    },
    onCameraStatusUpdate: (statusData) => {
      console.log('📡 [DASHBOARD] Camera status update:', statusData);
      // Update camera status in real-time
      setCameras(prev => 
        prev.map(camera => 
          camera.id === statusData.camera_id 
            ? { ...camera, connection_status: statusData.status }
            : camera
        )
      );
    }
  });

  // Combine API alerts with real-time alerts
  useEffect(() => {
    const combinedAlerts = [...realTimeAlerts, ...alerts];
    // Remove duplicates based on alert ID or timestamp
    const uniqueAlerts = combinedAlerts.filter((alert, index, self) => 
      index === self.findIndex(a => 
        a.id === alert.id || 
        (a.timestamp === alert.timestamp && a.camera_id === alert.camera_id)
      )
    );
    // Sort by timestamp (newest first) and limit to 5
    setWsAlerts(uniqueAlerts
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5)
    );
  }, [realTimeAlerts, alerts]);

  useEffect(() => {
    const load = async () => {
      try {
        const [camsRes, alertsRes] = await Promise.all([getCameras(), getAlerts()]);
        const list = camsRes.data || [];
        setCameras(list);
        // show up to 5 recent alerts on dashboard
        const alertsList = alertsRes.data || [];
        setAlerts(alertsList.slice(0, 5));
      } catch (e) {
        setCameras([]);
        setAlerts([]);
      }
    };
    load();
  }, []);

  const reloadCameras = async () => {
    try {
      const res = await getCameras();
      const newList = res.data || [];
      setCameras(newList);
    } catch {
      setCameras([]);
    }
  };

  const handleSignOut = () => {
    logoutUser();
    window.location.reload();
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      console.log(`🗑️ [DASHBOARD] Deleting alert ${alertId}`);
      await deleteAlert(alertId);
      
      // Remove alert from local state
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
      setWsAlerts(prev => prev.filter(alert => alert.id !== alertId));
      
      console.log('✅ [DASHBOARD] Alert deleted successfully');
    } catch (error) {
      console.error('❌ [DASHBOARD] Failed to delete alert:', error);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-900 text-gray-200 font-sans">
      <Sidebar user={user} onSignOut={handleSignOut} />
      <div className="flex-1 p-8 overflow-y-auto">
        <header className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-white">Dashboard</h2>
          <span className="text-sm text-gray-400">Welcome, {user.email}</span>
        </header>

        <div className="space-y-8">
          <CameraManagement cameras={cameras} user={user} onUpdateCameras={reloadCameras} />
          <AlertList alerts={wsAlerts} connectionState={connectionState} isConnected={isConnected} onDeleteAlert={handleDeleteAlert} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;